'use client';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { toolCategories } from '../data/tools';
import {
  Calculator,
  Code,
  Globe,
  Shield,
  Users,
  Palette,
  Megaphone,
  Briefcase,
  Search,
  X,
  Dice5,
  GraduationCap,
  Zap,
  Wrench,
  HeartPulse,
  DollarSign,
} from 'lucide-react';
import { Suspense, useState, useMemo, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

// Type definitions
interface Tool {
  id: string;
  name: string;
  description: string;
  path: string;
  keywords: string[];
}

interface Category {
  id: string;
  name: string;
  description: string;
  icon: string;
  tools: Tool[];
}

interface ToolWithCategory {
  tool: Tool;
  category: Category;
}

const categoryIcons = {
  developer: Code,
  conversion: Globe,
  'hash-calculators': Shield,
  'text-tools': Users,
  design: Palette,
  content: Megaphone,
  business: Briefcase,
  'random-generators': Dice5,
  education: GraduationCap,
  'web-seo': Globe,
  health: HeartPulse,
  finance: DollarSign,
  productivity: Zap,
  'personal-utilities': Wrench,
} as const;

function ToolsPageContent() {
  const searchParams = useSearchParams();
  const category = searchParams.get('category');
  const view = searchParams.get('view');
  const urlSearch = searchParams.get('search') ?? '';
  const [searchQuery, setSearchQuery] = useState(urlSearch);

  useEffect(() => {
    // Keep local search state in sync when query param changes (e.g. from header search)
    setSearchQuery(urlSearch);
  }, [urlSearch]);

  // Flatten all tools for search functionality
  const allTools = useMemo((): ToolWithCategory[] => {
    const tools: ToolWithCategory[] = [];
    toolCategories.forEach(cat => {
      cat.tools.forEach(tool => {
        tools.push({ tool, category: cat });
      });
    });
    return tools;
  }, []);

  // Filter tools based on search query and category
  const filteredTools = useMemo((): ToolWithCategory[] => {
    let tools = allTools;

    // Filter by category if specified
    if (category) {
      tools = tools.filter(item => item.category.id === category);
    }

    // Filter by search query if provided
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      tools = tools.filter(({ tool, category }) => {
        const searchableText = [
          tool.name,
          tool.description,
          ...tool.keywords,
          category.name,
          category.description
        ].join(' ').toLowerCase();

        return searchableText.includes(query);
      });
    }

    return tools;
  }, [allTools, category, searchQuery]);

  // Group filtered tools by category
  const filteredCategories = useMemo((): Category[] => {
    const categoryMap = new Map<string, Category>();

    filteredTools.forEach(({ tool, category }) => {
      if (!categoryMap.has(category.id)) {
        categoryMap.set(category.id, {
          ...category,
          tools: []
        });
      }
      categoryMap.get(category.id)!.tools.push(tool);
    });

    return Array.from(categoryMap.values());
  }, [filteredTools]);

  const hasSearchQuery = searchQuery.trim().length > 0;
  const hasCategoryFilter = category !== null;
  const isCategoriesView = view === 'categories' && !category && !hasSearchQuery;

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-10 px-4 dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-8">
        <div className="text-center">
          <h1 className="mb-3 text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-4xl">
            {category
              ? `${category.charAt(0).toUpperCase() + category.slice(1)} Tools`
              : isCategoriesView
                ? 'Browse by Category'
                : 'All Tools'}
          </h1>
          <p className="mx-auto max-w-3xl text-base text-slate-600 dark:text-slate-400 sm:text-lg">
            {category
              ? `Browse our collection of ${category} tools to simplify your daily tasks.`
              : isCategoriesView
                ? 'Explore curated categories across health, developer tools, encryption, converters, text utilities and more.'
                : 'Discover our complete collection of free online tools to simplify your daily tasks. No signup required!'}
          </p>

          {category && (
            <div className="mt-4">
              <Link
                href="/tools"
                className="inline-flex items-center font-medium text-blue-600 hover:text-blue-700 dark:text-indigo-300 dark:hover:text-indigo-200"
              >
                ← Back to All Categories
              </Link>
            </div>
          )}
        </div>

        {/* Search Bar */}
        {!isCategoriesView && (
          <div className="rounded-2xl border border-slate-200 bg-white/80 p-5 shadow-sm shadow-slate-200 dark:border-slate-800 dark:bg-slate-900/80 dark:shadow-none">
            <div className="mx-auto flex max-w-2xl flex-col gap-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <Input
                  type="text"
                  placeholder="Search tools by name, description, or keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-11 rounded-xl border-slate-200 bg-white pl-10 pr-10 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-50"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSearch}
                    className="absolute right-1.5 top-1/2 h-8 w-8 -translate-y-1/2 p-0 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Search Results Summary */}
              {(hasSearchQuery || hasCategoryFilter) && (
                <div className="text-center text-xs text-slate-600 dark:text-slate-400">
                  {hasSearchQuery && (
                    <span>
                      Found{" "}
                      <span className="font-semibold">
                        {filteredTools.length}
                      </span>{" "}
                      tool{filteredTools.length !== 1 ? "s" : ""}
                      {searchQuery && <span> matching "{searchQuery}"</span>}
                    </span>
                  )}
                  {hasSearchQuery && hasCategoryFilter && (
                    <span className="mx-2">•</span>
                  )}
                  {hasCategoryFilter && (
                    <span>
                      in{" "}
                      <span className="font-semibold">{category}</span> category
                    </span>
                  )}
                  {(hasSearchQuery || hasCategoryFilter) && (
                    <div className="mt-1">
                      <Link
                        href="/tools"
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-indigo-300 dark:hover:text-indigo-200"
                        onClick={() => setSearchQuery("")}
                      >
                        Clear all filters
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {isCategoriesView ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {toolCategories.map((cat) => {
              const IconComponent = categoryIcons[cat.id as keyof typeof categoryIcons];
              return (
                <Link
                  key={cat.id}
                  href={`/tools?category=${encodeURIComponent(cat.id)}`}
                  className="group rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm transition hover:-translate-y-1 hover:border-blue-500/70 hover:shadow-md dark:border-slate-800 dark:bg-slate-900/90 dark:hover:border-indigo-400/70"
                >
                  <div className="flex items-center">
                    {IconComponent && (
                      <div className="mr-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-slate-800 dark:text-indigo-300">
                        <IconComponent className="h-5 w-5" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                        {cat.name}
                      </h2>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {cat.tools.length} tools · {cat.description}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-slate-600 dark:text-slate-400">
              {hasSearchQuery
                ? `No tools found matching "${searchQuery}"${hasCategoryFilter ? ` in ${category} category` : ''}.`
                : 'No tools found in this category.'
              }
            </p>
            <div className="mt-4 space-x-4">
              {hasSearchQuery && (
                <Button
                  variant="outline"
                  onClick={clearSearch}
                  className="inline-block"
                >
                  Clear Search
                </Button>
              )}
              <Link
                href="/tools"
                className="inline-block rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                Browse All Tools
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-10">
            {filteredCategories.map((categoryData) => {
              const IconComponent = categoryIcons[categoryData.id as keyof typeof categoryIcons];
              return (
                <div
                  key={categoryData.id}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
                >
                  <div className="border-b border-slate-200 p-6 dark:border-slate-800">
                    <div className="flex items-center">
                      {IconComponent && (
                        <div className="mr-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-600 dark:bg-slate-800 dark:text-indigo-300">
                          <IconComponent className="h-5 w-5" />
                        </div>
                      )}
                      <div>
                        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
                          {categoryData.name}
                        </h2>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                          {categoryData.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
                    {categoryData.tools.map((tool) => (
                      <Link
                        key={tool.id}
                        href={tool.path}
                        className="group block rounded-xl border border-slate-200 p-4 transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:bg-blue-50 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-indigo-400 dark:hover:bg-slate-900/60"
                      >
                        <h3 className="text-base font-medium text-slate-900 transition-colors group-hover:text-blue-600 dark:text-slate-50 dark:group-hover:text-indigo-300">
                          {tool.name}
                        </h3>
                        <p className="mt-1 line-clamp-2 text-xs text-slate-600 dark:text-slate-400">
                          {tool.description}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-1">
                          {tool.keywords.slice(0, 3).map((keyword) => (
                            <span
                              key={keyword}
                              className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-800 dark:bg-slate-800 dark:text-slate-200"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                        <div className="mt-3 text-sm font-medium text-blue-600 group-hover:text-blue-700 dark:text-indigo-300 dark:group-hover:text-indigo-200">
                          Try Tool →
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ToolsPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Loading...</h1>
          </div>
        </div>
      </div>
    }>
      <ToolsPageContent />
    </Suspense>
  );
}
